// Valepaskahousu (decision 2026-09-30): the Finnish bluffing card game with the rules of the
// user's group (valepaskahousu-suunnitelma.md). Pure state machine shared by the server (rules,
// computer) and the UI (claims allowed, card names). The server keeps the hidden cards.

/** A card 0–51: rank = 2 + (card % 13) (2–14, 14 = ace), suit = floor(card / 13). */
export type Card = number;
export const rankOf = (c: Card) => 2 + (c % 13);
export const suitOf = (c: Card) => Math.floor(c / 13);
export const HAND_SIZE = 5;
/** Time to doubt the latest batch (user request 2026-09-30: 7 s); the next player waits this long. */
export const DOUBT_WINDOW_MS = 7000;
/**
 * After a doubt the game pauses while everybody sees it (user request 2026-09-30): "X doubts!" for
 * 3 s, then the doubted cards are turned over and shown. Nobody can play meanwhile.
 */
export const DOUBT_SUSPENSE_MS = 3000;
export const DOUBT_REVEAL_MS = DOUBT_SUSPENSE_MS + 3500;
/** A burning card on an empty table is shown this long before play continues. */
export const SPECIAL_REVEAL_MS = 3500;
const TEN = 10;
const ACE = 14;
const isFace = (r: number) => r >= 11 && r <= 13;

export interface Batch {
  by: number;
  /** The claimed rank. */
  claim: number;
  cards: Card[];
}

export type ValeEvent = (
  | { kind: 'burn'; by: number; reason: 'ten' | 'ace' | 'four' }
  | { kind: 'doubt'; doubter: number; accused: number; truthful: boolean; cards: Card[]; claim: number; pickedBy: number; pileSize: number }
  | { kind: 'empty_special'; by: number; truthful: boolean; cards: Card[]; claim: number; receiver: number | null }
  | { kind: 'out'; userId: number; place: number }
) & {
  /** The state sequence when the event happened (set by the server; the UI animates new ones). */
  seq?: number;
};

export interface ValeState {
  /** In turn order. */
  players: number[];
  hands: Map<number, Card[]>;
  deck: Card[];
  /** Batches on the table, oldest first. */
  pile: Batch[];
  /** The last batch can still be doubted (until the next play or a doubt). */
  open: boolean;
  /** The last batch burns the pile once nobody doubted it (the burner plays next). */
  pendingBurn: 'ten' | 'ace' | 'four' | null;
  /** Players who got rid of their cards, in order. */
  outOrder: number[];
  /** Players removed from the game by the host (resigned or timed out). */
  gone: Set<number>;
  /** The player whose turn it is. */
  turn: number;
  /** Server time of the last play (the next player waits DOUBT_WINDOW_MS). */
  lastPlayAt: number;
  /** Nobody plays before this server time (the doubt reveal); 0 = no pause. */
  pauseUntil: number;
  /** Increases with every change; doubts refer to it. */
  seq: number;
  events: ValeEvent[];
}

export type ValeAction = { type: 'play'; cards: Card[]; claim: number } | { type: 'doubt'; seq: number };

export function newDeck(rand: (n: number) => number): Card[] {
  const deck = Array.from({ length: 52 }, (_, i) => i);
  for (let i = deck.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  return deck;
}

export function initialState(players: readonly number[], rand: (n: number) => number, now = Date.now()): ValeState {
  const deck = newDeck(rand);
  const hands = new Map<number, Card[]>();
  for (const p of players) hands.set(p, deck.splice(0, HAND_SIZE));
  return {
    players: [...players],
    hands,
    deck,
    pile: [],
    open: false,
    pendingBurn: null,
    outOrder: [],
    gone: new Set(),
    turn: players[0]!,
    lastPlayAt: now - DOUBT_WINDOW_MS,
    pauseUntil: 0,
    seq: 0,
    events: [],
  };
}

/** The player in turn may play from this server time on: after the doubt window and any reveal pause. */
export const playableAt = (s: ValeState) => Math.max(s.lastPlayAt + DOUBT_WINDOW_MS, s.pauseUntil ?? 0);

/**
 * A burning batch that nobody doubted during the window has burned (fix 2026-09-30: the pile
 * disappears then, not only when the burner plays next). The state catches up at the next action
 * (`apply`); views show it at once.
 */
export const isBurned = (s: ValeState, now: number) => s.pendingBurn !== null && now >= s.lastPlayAt + DOUBT_WINDOW_MS;

export const topBatch = (s: ValeState): Batch | null => s.pile[s.pile.length - 1] ?? null;

/** Consecutive claimed cards of the top rank (for "four of a kind"). */
export function topRun(s: ValeState): number {
  const top = topBatch(s);
  if (!top) return 0;
  let n = 0;
  for (let i = s.pile.length - 1; i >= 0 && s.pile[i]!.claim === top.claim; i--) n += s.pile[i]!.cards.length;
  return n;
}

/** Ranks the player may claim now (the table is empty while a burn is pending). */
export function allowedClaims(s: ValeState): number[] {
  const top = s.pendingBurn ? null : topBatch(s);
  const ranks = Array.from({ length: 13 }, (_, i) => i + 2);
  if (!top) return ranks;
  const t = top.claim;
  if (t === 2) return [2];
  return ranks.filter((r) => {
    if (r === 2) return true;
    if (r === TEN) return t >= 3 && t <= 9;
    if (r === ACE) return isFace(t);
    return r >= t;
  });
}

export const cardCount = (s: ValeState, userId: number) => s.hands.get(userId)?.length ?? 0;
/** Still in the game: not out, not removed. Players with no cards wait for their last batch. */
export const inGame = (s: ValeState, userId: number) => !s.outOrder.includes(userId) && !s.gone.has(userId);
/** Can take a turn: in the game and holding cards. */
const canPlay = (s: ValeState, userId: number) => inGame(s, userId) && cardCount(s, userId) > 0;

/** The next player after `userId` who can play (null if none). */
export function nextPlayer(s: ValeState, userId: number): number | null {
  const i = s.players.indexOf(userId);
  for (let k = 1; k <= s.players.length; k++) {
    const p = s.players[(i + k) % s.players.length]!;
    if (canPlay(s, p)) return p;
  }
  return null;
}

function refill(s: ValeState, userId: number): void {
  const hand = s.hands.get(userId)!;
  while (hand.length < HAND_SIZE && s.deck.length > 0) hand.push(s.deck.shift()!);
}

/** Players with no cards left (and an empty deck) whose last batch can no longer be doubted are out. */
function settleOuts(s: ValeState): void {
  if (s.deck.length > 0) return;
  for (const p of s.players) {
    if (!inGame(s, p) || cardCount(s, p) > 0) continue;
    const top = topBatch(s);
    if (s.open && top?.by === p) continue; // the last batch may still be doubted
    s.outOrder.push(p);
    s.events.push({ kind: 'out', userId: p, place: s.outOrder.length });
  }
}

function burn(s: ValeState): void {
  s.pile = [];
  s.pendingBurn = null;
}

/** Players still in the game (with or without cards). */
export const remaining = (s: ValeState) => s.players.filter((p) => inGame(s, p));

/** The game is over when at most one player is still in it. */
export const isOver = (s: ValeState) => remaining(s).length <= 1;

export type ApplyResult = { ok: true } | { ok: false; error: 'not_your_turn' | 'invalid_move' | 'too_fast' | 'stale' };

/** Validates without changing anything. */
export function check(s: ValeState, userId: number, action: ValeAction, now: number): ApplyResult {
  if (action.type === 'doubt') {
    const top = topBatch(s);
    if (action.seq !== s.seq || !s.open || !top) return { ok: false, error: 'stale' };
    // A burning batch can be doubted only during the window; after it the pile is gone.
    if (isBurned(s, now)) return { ok: false, error: 'stale' };
    if (top.by === userId || !inGame(s, userId)) return { ok: false, error: 'invalid_move' };
    return { ok: true };
  }
  if (s.turn !== userId) return { ok: false, error: 'not_your_turn' };
  if (now < playableAt(s)) return { ok: false, error: 'too_fast' };
  const hand = s.hands.get(userId) ?? [];
  const { cards, claim } = action;
  if (cards.length === 0 || new Set(cards).size !== cards.length || !cards.every((c) => hand.includes(c))) return { ok: false, error: 'invalid_move' };
  if (!Number.isInteger(claim) || !allowedClaims(s).includes(claim)) return { ok: false, error: 'invalid_move' };
  return { ok: true };
}

/**
 * Applies a checked action. Randomness is not needed: the deck was shuffled at the start, so the
 * same actions always give the same state (the server stores the actions and replays them).
 */
export function apply(s: ValeState, userId: number, action: ValeAction, now: number): void {
  s.seq++;
  if (action.type === 'doubt') return doubt(s, userId, now);

  // A new play closes the previous batch; a pending burn happens now.
  s.open = false;
  if (s.pendingBurn) burn(s);
  settleOuts(s);

  const hand = s.hands.get(userId)!;
  const { cards, claim } = action;
  s.hands.set(
    userId,
    hand.filter((c) => !cards.includes(c)),
  );
  const truthful = cards.every((c) => rankOf(c) === claim);
  s.lastPlayAt = now;

  if (s.pile.length === 0 && (claim === TEN || claim === ACE || (claim !== 2 && cards.length >= 4))) {
    // Burning card(s) on an empty table: checked at once. True → the next player takes them and the
    // player plays again; a lie → the cards come back and the next player continues.
    const receiver = nextPlayer(s, userId);
    if (truthful && receiver !== null) {
      s.hands.get(receiver)!.push(...cards);
      refill(s, userId);
      s.events.push({ kind: 'empty_special', by: userId, truthful, cards, claim, receiver });
      s.lastPlayAt = now - DOUBT_WINDOW_MS; // checked at once: nothing to doubt
      s.pauseUntil = now + SPECIAL_REVEAL_MS;
      s.turn = cardCount(s, userId) > 0 ? userId : (nextPlayer(s, userId) ?? userId);
    } else {
      s.hands.get(userId)!.push(...cards);
      refill(s, userId);
      s.events.push({ kind: 'empty_special', by: userId, truthful, cards, claim, receiver: null });
      s.lastPlayAt = now - DOUBT_WINDOW_MS; // checked at once: nothing to doubt
      s.pauseUntil = now + SPECIAL_REVEAL_MS;
      s.turn = nextPlayer(s, userId) ?? userId;
    }
    settleOuts(s);
    return;
  }

  s.pile.push({ by: userId, claim, cards });
  refill(s, userId);
  s.open = true;
  const run = topRun(s);
  s.pendingBurn = claim === TEN ? 'ten' : claim === ACE ? 'ace' : claim !== 2 && run >= 4 ? 'four' : null;
  if (s.pendingBurn) {
    s.events.push({ kind: 'burn', by: userId, reason: s.pendingBurn });
    // The burner continues (if they still have cards); the pile goes when they play.
    s.turn = cardCount(s, userId) > 0 ? userId : (nextPlayer(s, userId) ?? userId);
  } else {
    s.turn = nextPlayer(s, userId) ?? userId;
  }
}

function doubt(s: ValeState, doubter: number, now: number): void {
  // Nothing is left to doubt: only the reveal pause holds the game.
  s.lastPlayAt = now - DOUBT_WINDOW_MS;
  s.pauseUntil = now + DOUBT_REVEAL_MS;
  const top = topBatch(s)!;
  const truthful = top.cards.every((c) => rankOf(c) === top.claim);
  const loser = truthful ? doubter : top.by;
  const all = s.pile.flatMap((b) => b.cards);
  s.hands.get(loser)!.push(...all);
  s.events.push({ kind: 'doubt', doubter, accused: top.by, truthful, cards: top.cards, claim: top.claim, pickedBy: loser, pileSize: all.length });
  s.pile = [];
  s.pendingBurn = null;
  s.open = false;
  // The one who picked up loses the turn.
  settleOuts(s);
  s.turn = nextPlayer(s, loser) ?? loser;
}

/** A player leaves the game: their cards go under the deck. */
export function removePlayer(s: ValeState, userId: number): void {
  if (s.gone.has(userId)) return;
  s.gone.add(userId);
  s.deck.push(...(s.hands.get(userId) ?? []));
  s.hands.set(userId, []);
  if (s.open && topBatch(s)?.by === userId) s.open = false;
  if (s.turn === userId) s.turn = nextPlayer(s, userId) ?? userId;
  s.seq++;
}

/** Placement score: the first out gets the most; the one left with cards (paskahousu) 0. */
export function scoreOf(s: ValeState, userId: number): number {
  const i = s.outOrder.indexOf(userId);
  return i < 0 ? 0 : s.players.length - i;
}

export const RANK_NAMES: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
export const rankLabel = (r: number) => RANK_NAMES[r] ?? String(r);
export const SUITS = ['♠', '♥', '♦', '♣'] as const;
